import React from 'react'
import { Link } from 'react-router-dom'
import { Video, ArrowLeft, PlayCircle, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../../components/layout/PublicFooter'
import { PublicNavigation } from '../../components/layout/PublicNavigation'
import { Button } from '../../components/ui/Button'

export const VideoTutorialsPage: React.FC = () => {
  const tutorials = [
    {
      title: 'Getting Started with Open1031',
      description: 'Learn how to create your account and navigate the platform',
      duration: '5:32',
      thumbnail: 'https://via.placeholder.com/400x225'
    },
    {
      title: 'Creating Your First Exchange',
      description: 'Step-by-step guide to starting a 1031 exchange',
      duration: '8:15',
      thumbnail: 'https://via.placeholder.com/400x225'
    },
    {
      title: 'Using the Exchange Hub',
      description: 'Master the Exchange Hub interface and workflow',
      duration: '12:45',
      thumbnail: 'https://via.placeholder.com/400x225'
    },
    {
      title: 'Document Management',
      description: 'How to upload, organize, and manage your exchange documents',
      duration: '6:20',
      thumbnail: 'https://via.placeholder.com/400x225'
    },
    {
      title: 'Working with Neela AI',
      description: 'Get the most out of your AI assistant for 1031 exchanges',
      duration: '7:50',
      thumbnail: 'https://via.placeholder.com/400x225'
    },
    {
      title: 'Understanding Deadlines',
      description: 'Learn about 45-day identification and 180-day exchange deadlines',
      duration: '9:30',
      thumbnail: 'https://via.placeholder.com/400x225'
    }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/documentation" className="inline-flex items-center text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Documentation
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <Video className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Video Tutorials
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Watch step-by-step video guides to master the Open1031 platform quickly.
            </p>
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tutorials.map((tutorial, index) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-xl transition-shadow group cursor-pointer"
              >
                <div className="relative aspect-video bg-slate-200 dark:bg-slate-700">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayCircle className="h-16 w-16 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {tutorial.duration}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    {tutorial.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    {tutorial.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Need More Help?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Check out our written documentation or contact support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/documentation">
              <Button className="bg-white text-[#69adba] hover:bg-slate-50">
                View Documentation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/support">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

